import React from 'react';
import { Modal, Form, Input, DatePicker, Button, Select, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { DealsApi } from 'api/api';
import { CreateDealInstallmentCommand, PaymentMethod } from 'api/models';
import { formatCurrency } from 'utility/utility';
import FeatherIcon from 'feather-icons-react';
import styled from 'styled-components';
import dayjs from 'dayjs';

// Add styled components
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FullWidthFormItem = styled(Form.Item)`
  .ant-select {
    width: 100%;
  }
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 24px;
`;

interface PaymentPlanModalProps {
  visible: boolean;
  dealId: number;
  onSuccess: () => void;
  onCancel: () => void;
  maxAmount: number;
}

const dealsApi = new DealsApi();

const PaymentPlanModal: React.FC<PaymentPlanModalProps> = ({ visible, dealId, onSuccess, onCancel, maxAmount }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const command: CreateDealInstallmentCommand = {
        dealId: dealId,
        expectedAmount: parseFloat(values.amount),
        dueDate: dayjs(values.dueDate).format('YYYY-MM-DD'),
        paymentMethod: values.paymentMethod,
        notes: values.notes,
      };

      await dealsApi.apiDealsIdPaymentsPost({
        id: dealId,
        createDealInstallmentCommand: command,
      });

      onSuccess();
      form.resetFields();
    } catch (error) {
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('deals:schedule-payment', 'Schedule Payment')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <StyledAlert
        type="warning"
        message={t('deals:pending-amount-warning', 'There is a total of {{amount}} to be scheduled yet.', {
          amount: formatCurrency(maxAmount),
        })}
        showIcon
      />
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="amount"
          label={t('deals:payment-amount', 'Payment Amount')}
          rules={[
            { required: true, message: t('validation:required', 'This field is required') },
            {
              pattern: /^\d+(\.\d{1,2})?$/,
              message: t('validation:invalid-amount', 'Please enter a valid amount'),
            },
            {
              validator: (_, value) => {
                const numValue = parseFloat(value);
                if (numValue > maxAmount) {
                  return Promise.reject(
                    t('validation:amount-exceeds-limit', 'Amount cannot exceed {{amount}}', {
                      amount: formatCurrency(maxAmount),
                    }),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input prefix="€" placeholder="0.00" />
        </Form.Item>

        <Form.Item
          name="dueDate"
          label={t('deals:due-date', 'Due Date')}
          rules={[{ required: true, message: t('validation:required', 'This field is required') }]}
        >
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>

        <FullWidthFormItem
          name="paymentMethod"
          label={t('deals:payment-method', 'Payment Method')}
          rules={[{ required: true, message: t('validation:required', 'This field is required') }]}
        >
          <Select>
            <Select.Option value={PaymentMethod.Cash}>
              <IconWrapper>
                <FeatherIcon icon="dollar-sign" size={16} />
                {t('payments-plan:payment-method-cash', 'Cash')}
              </IconWrapper>
            </Select.Option>
            <Select.Option value={PaymentMethod.Bank}>
              <IconWrapper>
                <FeatherIcon icon="credit-card" size={16} />
                {t('payments-plan:payment-method-bank', 'Bank Transfer')}
              </IconWrapper>
            </Select.Option>
            <Select.Option value={PaymentMethod.Crypto}>
              <IconWrapper>
                <FeatherIcon icon="shield" size={16} />
                {t('payments-plan:payment-method-crypto', 'Crypto')}
              </IconWrapper>
            </Select.Option>
            <Select.Option value={PaymentMethod.Other}>
              <IconWrapper>
                <FeatherIcon icon="help-circle" size={16} />
                {t('payments-plan:payment-method-other', 'Other')}
              </IconWrapper>
            </Select.Option>
          </Select>
        </FullWidthFormItem>

        <Form.Item name="notes" label={t('deals:notes', 'Notes')}>
          <Input.TextArea
            rows={4}
            placeholder={t('deals:payment-notes-placeholder', 'Enter any additional notes about this payment')}
          />
        </Form.Item>

        <div style={{ textAlign: 'right', marginTop: '24px' }}>
          <Button onClick={onCancel} style={{ marginRight: 8 }}>
            {t('global:cancel', 'Cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t('deals:schedule-payment', 'Schedule Payment')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default PaymentPlanModal;
